import type {
    AppColumn,
    AppAggregateValue,
    AppGroupLeaf,
    AppGroupNode,
    AppScalarType,
    AppScalarValue,
} from './AppTable.types';
import { DateTime } from 'luxon';
import { formatAppScalar } from './AppTable.model';

export type AppGroupedRow<TRow> = AppGroupNode<TRow> | AppGroupLeaf<TRow>;

function groupingKey(value: AppScalarValue, scalarType: AppScalarType): string {
    if (value === null || value === undefined) {
        return JSON.stringify([scalarType, 'empty']);
    }

    if (scalarType === 'date' && value instanceof Date) {
        const date = DateTime.fromJSDate(value);

        return JSON.stringify([scalarType, date.isValid ? date.toFormat('yyyy-MM-dd') : `invalid:${String(value)}`]);
    }

    if (scalarType === 'date' && typeof value === 'string') {
        const date = DateTime.fromISO(value, {
            setZone: true,
        });

        return JSON.stringify([scalarType, date.isValid ? date.toFormat('yyyy-MM-dd') : `invalid:${value}`]);
    }

    if (value instanceof Date) {
        const date = DateTime.fromJSDate(value);

        return JSON.stringify([scalarType, date.isValid ? date.toMillis() : `invalid:${String(value)}`]);
    }

    return JSON.stringify([scalarType, typeof value, value]);
}

function groupLabel<TRow>(column: AppColumn<TRow>, value: AppScalarValue, row: TRow): string {
    if (value === null || value === undefined) {
        return 'Empty';
    }

    if (value === '') {
        return '(blank)';
    }

    if (column.scalarType === 'lookup') {
        return (
            column.lookup?.labelFor?.(value, row) ??
            column.lookup?.options?.find((option) => Object.is(option.value, value))?.label ??
            String(value)
        );
    }

    return (
        column.formatter?.(value, row) ??
        (column.scalarType === 'date' || column.scalarType === 'dateTime'
            ? formatAppScalar(value, column.scalarType)
            : String(value))
    );
}

function aggregateValues<TRow>(rows: readonly TRow[], columns: readonly AppColumn<TRow>[]): AppAggregateValue[] {
    return columns
        .filter((column) => column.kind !== 'action' && column.aggregate === 'sum' && column.scalarType === 'number')
        .map((column) => ({
            columnId: column.id,
            value: rows.reduce((sum, row) => {
                const value = column.accessor(row);

                return typeof value === 'number' && Number.isFinite(value) ? sum + value : sum;
            }, 0),
        }));
}

/** Build a full ordered export tree from filtered and sorted source rows. */
export function buildAppGroupTree<TRow>(
    rows: readonly TRow[],
    columns: readonly AppColumn<TRow>[],
    grouping: readonly string[],
    getRowId: (row: TRow) => string | number,
): readonly AppGroupedRow<TRow>[] {
    const groupColumns = [...new Set(grouping)]
        .map((id) => columns.find((column) => column.id === id))
        .filter((column): column is AppColumn<TRow> =>
            Boolean(column && column.kind !== 'action' && column.groupable !== false),
        );
    const buildLevel = (
        levelRows: readonly TRow[],
        depth: number,
        path: readonly string[],
    ): readonly AppGroupedRow<TRow>[] => {
        if (depth >= groupColumns.length) {
            return levelRows.map((row) => ({
                kind: 'leaf',
                rowId: getRowId(row),
                row,
            }));
        }

        const column = groupColumns[depth];
        const groups = new Map<string, { value: AppScalarValue; rows: TRow[] }>();

        for (const row of levelRows) {
            const value = column.accessor(row);
            const key = groupingKey(value, column.scalarType);
            const found = groups.get(key);

            if (found) {
                found.rows.push(row);
            } else {
                groups.set(key, {
                    value,
                    rows: [row],
                });
            }
        }

        return [...groups.entries()].map(([key, group]) => {
            const id = JSON.stringify([...path, JSON.stringify([column.id, key])]);
            const children = buildLevel(group.rows, depth + 1, [...path, JSON.stringify([column.id, key])]);
            const firstRow = group.rows[0];
            const label = groupLabel(column, group.value, firstRow);

            return {
                kind: 'group' as const,
                id,
                columnId: column.id,
                value: group.value,
                label,
                depth,
                leafCount: group.rows.length,
                children,
                aggregates: aggregateValues(group.rows, columns),
            };
        });
    };

    return groupColumns.length > 0
        ? buildLevel(rows, 0, [])
        : rows.map((row) => ({
              kind: 'leaf',
              rowId: getRowId(row),
              row,
          }));
}

export function flattenAppGroupTree<TRow>(
    nodes: readonly AppGroupedRow<TRow>[],
    collapsedIds: ReadonlySet<string>,
): readonly AppGroupedRow<TRow>[] {
    const flattened: AppGroupedRow<TRow>[] = [];

    for (const node of nodes) {
        flattened.push(node);

        if (node.kind === 'group' && !collapsedIds.has(node.id)) {
            flattened.push(...flattenAppGroupTree(node.children, collapsedIds));
        }
    }

    return flattened;
}

export function appGrandTotals<TRow>(rows: readonly TRow[], columns: readonly AppColumn<TRow>[]): AppAggregateValue[] {
    return aggregateValues(rows, columns);
}
